// SSO-first login: Google / Microsoft, or organization email via /emailLogin (no invite_id)
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Alert, Box, Divider, Link, Paper, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import AuthBrandedLayout from '../components/auth/AuthBrandedLayout';
import SsoProviderButtons from '../components/auth/SsoProviderButtons';
import { getPostLoginRedirect, clearPostLoginRedirect } from '../utils/auth';
import { isAdminUIDisabled } from '../config/runtimeConfig';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin } = useAuth();
  const [ssoError, setSsoError] = useState('');
  const [redirectPath] = useState(() => getPostLoginRedirect());

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const from = searchParams.get('from');

    if (from === 'gmail') {
      const redirectUri = searchParams.get('redirect_uri');
      const email = searchParams.get('email');
      const ssoNonce = searchParams.get('sso_nonce');
      const addonUser = searchParams.get('addon_user');

      if (redirectUri && email) {
        try {
          const decodedRedirectUri = decodeURIComponent(redirectUri);
          const decodedEmail = decodeURIComponent(email);

          localStorage.setItem('gmail_redirect_uri', decodedRedirectUri);
          localStorage.setItem('gmail_email', decodedEmail);
          localStorage.setItem('from_gmail', 'true');
          if (ssoNonce) localStorage.setItem('gmail_sso_nonce', ssoNonce);
          if (addonUser) localStorage.setItem('gmail_addon_user', decodeURIComponent(addonUser));

          const googleButton = document.getElementById('google-sso-button');
          if (googleButton) {
            googleButton.click();
          }
        } catch (error) {
          console.error('Error decoding or storing Gmail login data:', error);
          setSsoError('There was an issue initiating the login from Gmail.');
        }
      }
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      // Platform is admin-only now — everyone else works from Gmail/Outlook/Google Chat directly.
      if (!isAdminUIDisabled() && !isAdmin) {
        clearPostLoginRedirect();
        navigate('/adminOnly', { replace: true });
        return;
      }
      if (redirectPath) {
        clearPostLoginRedirect();
        navigate(redirectPath, { replace: true });
      } else {
        // If no redirect path is found, navigate to the default page
        navigate('/supportSpace', { replace: true });
      }
    }
  }, [isAuthenticated, isAdmin, navigate, redirectPath]);

  return (
    <AuthBrandedLayout>
      <Paper
        elevation={4}
        sx={{
          p: 4,
          maxWidth: 440,
          width: '100%',
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="h5" component="h1" align="center" sx={{ fontWeight: 700, mb: 1 }}>
          Log in to your account
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Sign in with Google or Microsoft, or use your organization email.
        </Typography>

        {ssoError ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSsoError('')}>
            {ssoError}
          </Alert>
        ) : null}

        <SsoProviderButtons onError={setSsoError} />

        <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
          <Divider sx={{ flex: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
            OR
          </Typography>
          <Divider sx={{ flex: 1 }} />
        </Box>

        <Box
          sx={{
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2,
            textAlign: 'center',
          }}
        >
          <Link
            component={RouterLink}
            to="/emailLogin"
            underline="hover"
            sx={{ fontWeight: 600 }}
          >
            Log in with Email
          </Link>
        </Box>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
          Don&apos;t have an account?{' '}
          <Link
            component={RouterLink}
            to="/companySignup"
            underline="none"
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              fontWeight: 500,
            }}
          >
            Sign up here
          </Link>
        </Typography>
      </Paper>
    </AuthBrandedLayout>
  );
};

export default Login;
